import React from "react"
import { useTheme } from "@chakra-ui/system"
import { MapStyleSetting, useMapSettings } from "../../components/providers/MapSettingsProvider"

interface HookProps {
  type: string
  isSelected: boolean
}

interface HookPropsZone {
  type: string
  isSelected: boolean
  isZoneMap?: boolean
}

const useDropZoneColors = ({ type, isSelected }: HookProps) => {
  const theme = useTheme()
  const lineColor = isSelected ? theme.colors.pink["500"] : "transparent"
  switch (type) {
    case "cycling_area":
      return {
        lineColor,
        fillColor: theme.colors.purple["500"],
        fillOutlineColor: theme.colors.purple["800"],
      }
    case "maintenance":
      return {
        lineColor,
        fillColor: theme.colors.gray["500"],
        fillOutlineColor: theme.colors.gray["800"],
      }
    default:
      return {
        lineColor,
        fillColor: theme.colors.blue["500"],
        fillOutlineColor: theme.colors.blue["800"],
      }
  }
}

const useDropZoneColorsGetter = () => {
  const theme = useTheme()
  return React.useCallback(
    ({ type, isSelected }: HookProps) => {
      const lineColor = isSelected ? theme.colors.pink["500"] : "transparent"
      switch (type) {
        case "cycling_area":
          return {
            lineColor,
            fillColor: theme.colors.purple["500"],
            fillOutlineColor: theme.colors.purple["800"],
          }
        case "maintenance":
          return {
            lineColor,
            fillColor: theme.colors.gray["500"],
            fillOutlineColor: theme.colors.gray["800"],
          }
        default:
          return {
            lineColor,
            fillColor: theme.colors.blue["500"],
            fillOutlineColor: theme.colors.blue["800"],
          }
      }
    },
    [theme],
  )
}

interface CalcFillOpacityArgs {
  isSelected?: boolean
  isZoneMap?: boolean
  mapStyleSetting: MapStyleSetting
}

const calcFillOpacity = ({ isSelected, isZoneMap, mapStyleSetting }: CalcFillOpacityArgs) => {
  if (isSelected) return isZoneMap ? 0.7 : 0.4
  if (mapStyleSetting === "satellite") return isZoneMap ? 0.6 : 0.3
  if (mapStyleSetting === "dark") return isZoneMap ? 0.6 : 0.3
  return isZoneMap ? 0.5 : 0.15
}

export const useFillOpacity = (args?: { isSelected?: boolean; isZoneMap?: boolean }) => {
  const { mapStyleSetting } = useMapSettings()
  return calcFillOpacity({
    isSelected: args?.isSelected,
    isZoneMap: args?.isZoneMap,
    mapStyleSetting,
  })
}

export const useFillOpacityGetter = (args?: { isZoneMap?: boolean }) => {
  const { mapStyleSetting } = useMapSettings()
  return React.useCallback(
    (callbackArgs?: { isSelected?: boolean }) => {
      return calcFillOpacity({
        isSelected: callbackArgs?.isSelected,
        isZoneMap: args?.isZoneMap,
        mapStyleSetting,
      })
    },
    [args?.isZoneMap, mapStyleSetting],
  )
}

export const useDropZonePaint = ({ type, isSelected, isZoneMap }: HookPropsZone) => {
  const { fillColor, fillOutlineColor, lineColor } = useDropZoneColors({ type, isSelected })
  const fillOpacity = useFillOpacity({ isSelected, isZoneMap })
  return { fillColor, fillOpacity, fillOutlineColor, lineColor }
}

export const useDropZonePaintGetter = (args?: { isZoneMap?: boolean }) => {
  const getDropZoneColors = useDropZoneColorsGetter()
  const getFillOpacity = useFillOpacityGetter({ isZoneMap: args?.isZoneMap })
  return React.useCallback(
    ({ type, isSelected }: HookProps) => {
      const { fillColor, fillOutlineColor, lineColor } = getDropZoneColors({ type, isSelected })
      const fillOpacity = getFillOpacity({ isSelected })
      return { fillColor, fillOpacity, fillOutlineColor, lineColor }
    },
    [getDropZoneColors, getFillOpacity],
  )
}
